///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Loader } from "@inweb/viewer-core";
import { Viewer } from "../Viewer";
import { ModelImpl } from "../Models/ModelImpl";

export class VSFXCloudStreamingLoader extends Loader {
  public viewer: Viewer;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  override isSupport(file: any): boolean {
    return (
      typeof file === "object" &&
      typeof file.database === "string" &&
      typeof file.downloadResource === "function" &&
      /.vsfx$/i.test(file.database) &&
      this.viewer.options.enableStreamingMode === true &&
      this.viewer.options.enablePartialMode === false &&
      !/.rcs$/i.test(file.name)
    );
  }

  override async load(model: any): Promise<this> {
    if (!this.viewer.visualizeJs) return this;

    const visLib = this.viewer.visLib();
    const visViewer = this.viewer.visViewer();

    let isServiceDataReady = false;

    const chunkLoadHandler = (progress: number, chunk: Uint8Array) => {
      if (!this.viewer.visualizeJs) return;

      let status: any;
      try {
        status = visViewer.parseVsfx(chunk);
      } catch (error: any) {
        console.error("VSFX parse error.", error);
        throw error;
      }

      this.viewer.emitEvent({ type: "geometryprogress", data: progress, file: model.file, model });

      let isDatabaseChunk = false;
      if (
        status === visLib.DatabaseStreamStatus.ReadyServiceData ||
        (status === visLib.DatabaseStreamStatus.Complete && !isServiceDataReady)
      ) {
        isServiceDataReady = true;
        isDatabaseChunk = true;
      }

      if (isDatabaseChunk) {
        const modelImpl = new ModelImpl();
        modelImpl.id = model.file.id;

        this.viewer.models.push(modelImpl);

        this.viewer.syncOptions();
        this.viewer.syncOverlay();

        this.viewer.emitEvent({ type: "databasechunk", data: chunk, file: model.file, model });
        this.viewer.update(true);
      } else {
        this.viewer.emitEvent({ type: "geometrychunk", data: chunk, file: model.file, model });
        this.viewer.update();
      }
    };

    await model.downloadResource(model.database, chunkLoadHandler, this.abortController.signal);

    return this;
  }
}
